import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, MapPin, Calendar, Clock, Users, Check, X, ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FadeUp = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }} className={className}>
      {children}
    </motion.div>
  );
};

/* ── Program text renderer ──────────────────────────────────────────────────── */
function ProgramRenderer({ text, truncate = false }) {
  if (!text) return null;
  const lines = text.split("\n");
  const displayLines = truncate ? lines.slice(0, 6) : lines;
  const hasMore = truncate && lines.length > 6;

  return (
    <div className="space-y-1">
      {displayLines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={i} className="h-2" />;
        // Emoji section header (e.g. "📍 Dla kogo?", "🧪 Moduł 1:")
        const emojiMatch = trimmed.match(/^(\p{Emoji})\s(.+)/u);
        if (emojiMatch) return (
          <div key={i} className="flex items-start gap-2 pt-2">
            <span className="text-base flex-shrink-0">{emojiMatch[1]}</span>
            <span className="font-outfit font-semibold text-white text-sm">{emojiMatch[2]}</span>
          </div>
        );
        // Bullet point (• or - at start)
        if (trimmed.startsWith("•") || trimmed.startsWith("-")) return (
          <div key={i} className="flex items-start gap-2 pl-2">
            <span className="text-violet-400 flex-shrink-0 mt-1 text-xs">•</span>
            <span className="text-white/60 text-xs leading-relaxed">{trimmed.replace(/^[•\-]\s*/, "")}</span>
          </div>
        );
        // Bold-like heading (all caps or ends with ":")
        if (trimmed === trimmed.toUpperCase() && trimmed.length > 3 && !trimmed.includes("•")) return (
          <p key={i} className="text-white/80 text-xs font-outfit font-semibold uppercase tracking-[0.1em] pt-1">{trimmed}</p>
        );
        // Normal text
        return <p key={i} className="text-white/50 text-xs leading-relaxed">{trimmed}</p>;
      })}
      {hasMore && <p className="text-white/25 text-xs">...</p>}
    </div>
  );
}

/* ── Purchase modal ─────────────────────────────────────────────────────────── */
function PurchaseModal({ training, onClose }) {
  const [form, setForm] = useState({ client_name: "", client_email: "", client_phone: "" });
  const [step, setStep] = useState(1); // 1=info, 2=form, 3=payment, 4=success
  const [purchase, setPurchase] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showFullProgram, setShowFullProgram] = useState(false);

  const deposit = training.deposit_amount || 0;
  const remaining = deposit > 0 ? (training.price - deposit) : 0;
  const payNow = deposit > 0 ? deposit : training.price;

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await axios.post(`${API}/training-purchases`, { training_id: training.id, ...form });
      setPurchase(data); setStep(3);
    } catch (err) { setError(err.response?.data?.detail || "Błąd. Spróbuj ponownie."); }
    finally { setLoading(false); }
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      if (purchase && !purchase.is_mock) {
        // Real Przelewy24 — redirect to payment gateway
        window.location.href = purchase.payment_url;
      } else {
        // Mock flow — credentials not yet configured
        await axios.post(`${API}/training-purchases/${purchase.purchase_id}/confirm`);
        setStep(4);
      }
    } catch (err) { setError("Błąd płatności. Spróbuj ponownie."); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="purchase-modal" className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/85 backdrop-blur-sm overflow-y-auto">
      {/* Close button — fixed to viewport, always visible */}
      <button data-testid="modal-close" onClick={onClose}
        className="fixed top-4 right-4 z-[60] w-10 h-10 rounded-full flex items-center justify-center transition-all hover:bg-white/10"
        style={{ background: "rgba(0,0,0,0.8)", border: "1px solid rgba(255,255,255,0.25)" }}>
        <X size={18} className="text-white" />
      </button>

      <div className="bg-[#0A0A0A] border border-violet-600/20 max-w-2xl w-full relative my-4"
        style={{ boxShadow: "0 0 60px rgba(139,0,255,0.1)" }}>

        {/* Modal header with training image */}
        {training.image && (
          <div className="aspect-video overflow-hidden relative">
            <img src={training.image} alt={training.title}
              className="w-full h-full object-cover opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent pointer-events-none" />
          </div>
        )}

        <div className="p-6 md:p-8">
        {/* STEP 1 — Training info (no form yet) */}
          {step === 1 && (
            <>
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <span className={`text-xs px-2 py-1 border ${training.is_online ? "border-blue-500/30 text-blue-400" : "border-violet-600/30 text-violet-400"}`}>
                  {training.is_online ? "Online" : "Stacjonarne"}
                </span>
                <span className={`text-xs font-outfit ${training.spots_available <= 2 ? "text-red-400" : "text-green-400"}`}>
                  {training.spots_available}/{training.spots_total} miejsc
                </span>
              </div>
              <h3 className="font-anton text-2xl md:text-3xl text-white uppercase leading-tight mb-3">{training.title}</h3>
              <p className="text-white/60 text-sm leading-relaxed mb-4">{training.description}</p>

              <div className="grid grid-cols-2 gap-2 text-xs text-white/40 mb-2">
                <div className="flex items-center gap-1.5"><Calendar size={11} /> {training.date}</div>
                <div className="flex items-center gap-1.5"><Clock size={11} /> {training.duration}h</div>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-white/40 mb-5">
                <MapPin size={11} /> {training.location}
              </div>

              {training.program && (
                <div className="border border-white/8 p-4 mb-5" style={{ background: "#111111" }}>
                  <button onClick={() => setShowFullProgram(!showFullProgram)}
                    className="flex justify-between items-center w-full text-left">
                    <p className="text-xs uppercase tracking-[0.15em] text-white/40">Program szkolenia</p>
                    {showFullProgram ? <ChevronUp size={14} className="text-white/30" /> : <ChevronDown size={14} className="text-white/30" />}
                  </button>
                  <div className={`mt-3 overflow-hidden transition-all ${showFullProgram ? "" : "max-h-24"}`}>
                    <ProgramRenderer text={training.program} truncate={!showFullProgram} />
                  </div>
                  {!showFullProgram && training.program.split("\n").length > 6 && (
                    <button onClick={() => setShowFullProgram(true)} className="text-violet-400 text-xs mt-2 hover:text-white transition-colors">
                      Czytaj cały program →
                    </button>
                  )}
                </div>
              )}

              {/* Price info */}
              <div className="border border-white/5 p-4 mb-6" style={{ background: "#111111" }}>
                {deposit > 0 ? (
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-white/50">Pełna cena</span><span className="text-white">{training.price} zł</span></div>
                    <div className="flex justify-between"><span className="text-green-400">Zadatek (płatny teraz)</span><span className="font-semibold text-green-400">{deposit} zł</span></div>
                    <div className="flex justify-between border-t border-white/5 pt-1.5"><span className="text-white/40">Reszta na miejscu</span><span className="text-white/60">{remaining} zł</span></div>
                  </div>
                ) : (
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">Cena szkolenia</span>
                    <span className="font-anton text-2xl text-white">{training.price} zł</span>
                  </div>
                )}
              </div>

              <button data-testid="purchase-register-btn" onClick={() => setStep(2)}
                className="btn-primary w-full justify-center">
                Zarejestruj się <ArrowRight size={14} />
              </button>
            </>
          )}

          {/* STEP 2 — Registration form */}
          {step === 2 && (
            <>
              <p className="text-xs uppercase tracking-[0.2em] text-violet-400 mb-2">Rejestracja</p>
              <h3 className="font-anton text-2xl text-white uppercase mb-1">{training.title}</h3>
              <p className="text-white/40 text-xs mb-5">{training.date} · {training.location}</p>
              <form data-testid="purchase-form" onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="text-xs uppercase tracking-[0.12em] text-white/40 block mb-2">Imię i nazwisko *</label>
                  <input data-testid="purchase-name" type="text" required value={form.client_name}
                    onChange={e => setForm({ ...form, client_name: e.target.value })}
                    className="input-dark" placeholder="Anna Kowalska" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.12em] text-white/40 block mb-2">E-mail *</label>
                  <input data-testid="purchase-email" type="email" required value={form.client_email}
                    onChange={e => setForm({ ...form, client_email: e.target.value })}
                    className="input-dark" placeholder="anna@example.com" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.12em] text-white/40 block mb-2">Telefon</label>
                  <input data-testid="purchase-phone" type="tel" value={form.client_phone}
                    onChange={e => setForm({ ...form, client_phone: e.target.value })}
                    className="input-dark" placeholder="+48 000 000 000" />
                </div>
                {error && <p className="text-red-400 text-sm">{error}</p>}
                <div className="flex gap-3">
                  <button type="button" onClick={() => setStep(1)} className="btn-secondary text-xs flex-shrink-0">Wstecz</button>
                  <button data-testid="purchase-next" type="submit" disabled={loading}
                    className="btn-primary flex-1 justify-center disabled:opacity-50">
                    {loading ? "Ładowanie..." : "Dalej — Płatność"} <ArrowRight size={14} />
                  </button>
                </div>
              </form>
            </>
          )}

          {/* STEP 3 — Payment summary */}
          {step === 3 && (
            <>
              <p className="text-xs uppercase tracking-[0.2em] text-violet-400 mb-2">Płatność</p>
              <h3 className="font-anton text-2xl text-white uppercase mb-5">Podsumowanie</h3>
              <div className="border border-white/8 p-5 mb-5 space-y-3">
                <div className="flex justify-between text-sm"><span className="text-white/50">Szkolenie</span><span className="text-white">{training.title}</span></div>
                <div className="flex justify-between text-sm"><span className="text-white/50">Data</span><span className="text-white">{training.date}</span></div>
                <div className="flex justify-between text-sm"><span className="text-white/50">Uczestnik</span><span className="text-white">{form.client_name}</span></div>
                <div className="border-t border-white/5 pt-3 space-y-2">
                  {deposit > 0 ? (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Pełna cena</span>
                        <span className="text-white">{training.price} zł</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-green-400 text-sm font-medium">Zadatek (opłacisz teraz)</span>
                        <span className="font-anton text-xl text-green-400">{deposit} zł</span>
                      </div>
                      <div className="flex justify-between text-sm border-t border-white/5 pt-2">
                        <span className="text-white/40">Reszta płatna na miejscu</span>
                        <span className="text-white/60">{remaining} zł</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between">
                      <span className="text-white/50 text-sm">Do zapłaty</span>
                      <span className="font-anton text-2xl text-white">{training.price} zł</span>
                    </div>
                  )}
                </div>
              </div>
              {/* P24 badge */}
              <div className="border border-white/5 p-4 mb-5" style={{ background: "#111111" }}>
                <div className="flex items-center gap-3 flex-wrap mb-2">
                  <span className="text-white/40 text-xs font-outfit">Płatności obsługuje:</span>
                  <div className="bg-white rounded px-2 py-1">
                    <img src="https://customer-assets.emergentagent.com/job_fryzjer-artysta/artifacts/6c4r4ws4_flagi_Przelewy24_2.png"
                      alt="Przelewy24" className="h-5 object-contain" />
                  </div>
                </div>
                <p className="text-white/50 text-xs font-outfit">
                  BLIK · mBank · ING · PKO · Pekao · Santander · Millennium i inne
                </p>
              </div>
              <div className="border border-yellow-500/20 bg-yellow-500/5 p-3 mb-5 text-xs text-yellow-500/70">
                {purchase?.is_mock
                  ? "Tryb testowy — płatności Przelewy24 aktywowane po konfiguracji kluczy API."
                  : "Zostaniesz przekierowana na bezpieczną stronę Przelewy24."}
              </div>
              {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
              <div className="flex flex-col gap-3">
                <button data-testid="payment-confirm" onClick={handlePayment} disabled={loading}
                  className="btn-primary justify-center disabled:opacity-50">
                  {loading ? "Przetwarzanie..." : `${purchase?.is_mock ? "[TEST] " : ""}Zapłać ${deposit > 0 ? `zadatek ${deposit} zł` : `${training.price} zł`} przez Przelewy24`} <ArrowRight size={14} />
                </button>
                <button onClick={() => setStep(2)} className="btn-secondary justify-center text-xs">Wstecz</button>
              </div>
            </>
          )}

          {/* STEP 4 — Success */}
          {step === 4 && (
            <div data-testid="purchase-success" className="text-center py-6">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{ background: "rgba(139,0,255,0.15)", border: "1px solid rgba(139,0,255,0.3)" }}>
                <Check size={28} className="text-violet-500" />
              </div>
              <h3 className="font-anton text-2xl text-white uppercase mb-2">Rejestracja Potwierdzona!</h3>
              <p className="text-white/60 text-sm mb-1">Potwierdzenie zostanie wysłane na:</p>
              <p className="text-white text-sm mb-3">{form.client_email}</p>
              {deposit > 0 && (
                <div className="border border-green-500/20 p-3 mb-4 text-left" style={{ background: "rgba(34,197,94,0.05)" }}>
                  <p className="text-green-400 text-xs font-medium mb-1">Zadatek {deposit} zł — opłacony</p>
                  <p className="text-white/40 text-xs">Pozostałe {remaining} zł płatne na miejscu po szkoleniu.</p>
                </div>
              )}
              <p className="text-white/40 text-xs mb-6">Do zobaczenia na szkoleniu!</p>
              <button data-testid="purchase-done" onClick={onClose} className="btn-primary mx-auto">
                Zamknij
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Training card ──────────────────────────────────────────────────────────── */
function TrainingCard({ t, i, onBuy }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <FadeUp delay={i * 0.1}>
      <div className="border border-violet-600/20 overflow-hidden flex flex-col group"
        style={{ background: "#0A0A0A", boxShadow: "0 0 30px rgba(139,0,255,0.05)" }}>

        {/* Photo */}
        {t.image && (
          <div className="aspect-video overflow-hidden flex-shrink-0">
            <img src={t.image} alt={t.title}
              className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-500" />
          </div>
        )}

        <div className="p-5 md:p-6 flex flex-col flex-1">
          {/* Badges */}
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span className={`text-xs px-2 py-0.5 border ${t.is_online ? "border-blue-500/30 text-blue-400" : "border-violet-600/30 text-violet-400"}`}>
              {t.is_online ? "Online" : "Stacjonarne"}
            </span>
            <span className={`text-xs font-outfit font-medium ${t.spots_available <= 2 ? "text-red-400" : "text-green-400"}`}>
              {t.spots_available}/{t.spots_total} miejsc
            </span>
          </div>

          {/* Title */}
          <h3 className="font-anton text-2xl md:text-[1.6rem] text-white uppercase leading-tight mb-3">{t.title}</h3>

          {/* Description */}
          <p className="text-white/60 text-sm leading-relaxed mb-4 line-clamp-5">{t.description}</p>

          {/* Meta */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-1 text-xs text-white/40">
            <div className="flex items-center gap-1.5 min-w-0"><Calendar size={11} className="flex-shrink-0" /><span className="truncate">{t.date}</span></div>
            <div className="flex items-center gap-1.5"><Clock size={11} className="flex-shrink-0" />{t.duration}h</div>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-white/40 mb-4">
            <MapPin size={11} className="flex-shrink-0" /><span className="truncate">{t.location}</span>
          </div>

          {/* Program section */}
          {t.program && (
            <div className="border-t border-white/5 pt-4 mb-5 flex-1">
              <button onClick={() => setExpanded(!expanded)}
                className="flex justify-between items-center w-full text-left mb-2 group">
                <p className="text-xs uppercase tracking-[0.15em] text-white/30 group-hover:text-white/50 transition-colors">Program</p>
                {expanded ? <ChevronUp size={12} className="text-white/25" /> : <ChevronDown size={12} className="text-white/25" />}
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${expanded ? "max-h-[600px]" : "max-h-20"}`}>
                <ProgramRenderer text={t.program} truncate={!expanded} />
              </div>
              {!expanded && t.program.split("\n").length > 4 && (
                <button onClick={() => setExpanded(true)}
                  className="text-violet-400/70 text-xs mt-1 hover:text-violet-400 transition-colors">
                  Pokaż cały program ↓
                </button>
              )}
            </div>
          )}

          {/* Footer: price + CTA */}
          <div className="flex justify-between items-center pt-4 border-t border-white/5 mt-auto">
            <div className="font-anton text-3xl text-white leading-none">
              {t.deposit_amount > 0 ? (
                <div>
                  <div>{t.deposit_amount} <span className="text-base font-outfit font-light">zł zadatku</span></div>
                  <div className="text-white/30 text-sm font-outfit font-light">pełna cena: {t.price} zł</div>
                </div>
              ) : (
                <span>{t.price} <span className="text-base font-outfit font-light">zł</span></span>
              )}
            </div>
            <button data-testid={`buy-training-${i}`}
              onClick={() => t.spots_available > 0 ? onBuy(t) : null}
              disabled={t.spots_available <= 0}
              className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0">
              {t.spots_available <= 0 ? "Brak miejsc" : "Kup Szkolenie"} <ArrowRight size={12} />
            </button>
          </div>
        </div>
      </div>
    </FadeUp>
  );
}

/* ── Main page ──────────────────────────────────────────────────────────────── */
export default function Training() {
  const [trainings, setTrainings] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/trainings`).then(r => { setTrainings(r.data); setLoading(false); }).catch(console.error);
  }, []);

  return (
    <div className="bg-brand-base min-h-screen">
      <Navbar />

      {/* Hero */}
      <section data-testid="training-hero" className="relative min-h-[50vh] flex items-end pt-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1776111885236-fe77b7e3f843?q=80&w=1600&auto=format&fit=crop"
            alt="Szkolenia" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0.6) 0%, rgba(5,5,5,1) 80%)" }} />
        </div>
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 pb-14 w-full relative">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="font-outfit text-xs uppercase tracking-[0.3em] text-violet-400 mb-4">Edukacja</p>
            <h1 className="font-anton text-6xl sm:text-8xl md:text-[10vw] text-white uppercase leading-none">SZKOLENIA</h1>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-12 max-w-screen-2xl mx-auto px-6 md:px-12">
        <FadeUp className="max-w-2xl">
          <p className="text-white/60 text-base leading-relaxed">
            Dzielę się moją wiedzą z fryzjerami i kolorystami. Intensywne, praktyczne szkolenia — stacjonarne i online.
            Każdy uczestnik wychodzi z konkretnymi umiejętnościami, nie tylko teorią.
          </p>
        </FadeUp>
      </section>

      {/* Training cards */}
      <section data-testid="training-list" className="pb-24 max-w-screen-2xl mx-auto px-6 md:px-12">
        {loading ? (
          <div className="py-20 text-center text-white/40">Ładowanie...</div>
        ) : trainings.length === 0 ? (
          <div className="py-20 text-center text-white/30">Brak aktualnych szkoleń. Sprawdź wkrótce.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {trainings.map((t, i) => (
              <TrainingCard key={t.id} t={t} i={i} onBuy={setSelected} />
            ))}
          </div>
        )}
      </section>

      {/* Why attend */}
      <section data-testid="training-why" className="py-24" style={{ background: "#0A0A0A" }}>
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <FadeUp>
            <p className="font-outfit text-xs uppercase tracking-[0.3em] text-violet-400 mb-4">Dlaczego warto</p>
            <h2 className="font-anton text-4xl text-white uppercase mb-12">CO DOSTAJESZ</h2>
          </FadeUp>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: <Users size={20} />, title: "Małe grupy", desc: "Maksymalnie 8 osób - indywidualne podejście i czas na pytania." },
              { icon: <Check size={20} />, title: "Certyfikat", desc: "Po ukończeniu szkolenia otrzymujesz certyfikat uczestnictwa." },
              { icon: <Calendar size={20} />, title: "Materiały", desc: "Wszystkie materiały edukacyjne i produkty do ćwiczeń włączone." },
              { icon: <ArrowRight size={20} />, title: "Wsparcie po", desc: "Konsultacje online przez miesiąc po szkoleniu." },
            ].map((w, i) => (
              <FadeUp key={i} delay={i * 0.1}>
                <div className="border border-white/5 p-6 card-hover">
                  <div className="text-violet-500 mb-4">{w.icon}</div>
                  <h3 className="font-outfit font-semibold text-white mb-2">{w.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">{w.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {selected && <PurchaseModal training={selected} onClose={() => setSelected(null)} />}
      <Footer />
    </div>
  );
}
